/**
 * Technische Anlagen / Zähler / Energieausweis — list view state: search, filters,
 * column sorting and paging over the rows already loaded for the property. Pure
 * functions; the DOM wiring lives in `technicalAssetsSection.js`, the table shell
 * in `technicalAssetsSection.layout.js`. Cross-file references stay inside
 * function bodies so gulp concat order does not matter.
 */

/** Rows per page; same as the Versicherungen table. */
const TECHNICAL_ASSETS_PAGE_SIZE = 10

/** @returns {{ search: string, building: string, kind: string, sort: { key: string, dir: 'asc'|'desc' }|null, page: number }} */
function emptyTechnicalAssetView() {
  return { search: '', building: '', kind: '', sort: null, page: 1 }
}

function taSearchNormalize(value) {
  return String(value == null ? '' : value)
    .toLocaleLowerCase('de')
    .trim()
}

/**
 * Everything a user may type to find the row: name, kind, building, type-field
 * values, every resolved contact name and the plan names.
 */
function technicalAssetSearchText(asset, ctx) {
  const names = (ctx && ctx.contactNames) || {}
  const contactName = (id) =>
    id == null ? '' : names[String(id)] || `Kontakt #${id}`
  const contactIds = [
    asset.manufacturerContactId,
    asset.assignedMaintenanceCompanyContactId,
    ...(asset.maintenanceCompanyContactIds || []),
    ...(asset.additionalMaintenanceCompanyContactIds || []),
    ...(asset.maintenanceStaffContactIds || []),
  ]
  const plans = asset.maintenancePlans || []
  plans.forEach((plan) => {
    if (!plan || !plan.details) return
    contactIds.push(plan.details.assignedMaintenanceCompanyContactId)
    contactIds.push(
      ...(plan.details.additionalMaintenanceCompanyContactIds || [])
    )
  })
  const highlights =
    (ctx &&
      ctx.highlightsByAssetId &&
      ctx.highlightsByAssetId[String(asset.id)]) ||
    []
  highlights.forEach((h) => {
    if (h.input === 'contact') contactIds.push(h.value)
    if (h.input === 'contactMulti' && Array.isArray(h.value)) {
      contactIds.push(...h.value)
    }
  })
  const typeFields =
    asset.typeFields && typeof asset.typeFields === 'object'
      ? asset.typeFields
      : {}
  const fieldValues = Object.keys(typeFields)
    .filter((key) => !/ContactId(s)?$/.test(key))
    .map((key) => typeFields[key])
    .filter((value) => typeof value === 'string' || typeof value === 'number')
  return taSearchNormalize(
    [
      asset.name,
      asset.typ,
      `TA-${asset.id}`,
      `Z-${asset.id}`,
      asset.buildingLabel,
      asset.buildingHrId,
      ...fieldValues,
      ...highlights.filter((h) => h.input === 'select').map((h) => h.value),
      ...contactIds.filter((id) => id != null).map(contactName),
      ...plans.map((plan) => plan && plan.name),
    ]
      .filter((part) => part != null && part !== '')
      .join(' ')
  )
}

/** Rows matching the search text, the building filter and the kind filter. */
function filterTechnicalAssetRows(rows, view, ctx) {
  const search = taSearchNormalize(view.search)
  const building = String(view.building || '')
  const kind = String(view.kind || '')
  return rows.filter(
    (row) =>
      (!building || String(row.buildingId) === building) &&
      (!kind || String(row.typ) === kind) &&
      (!search || technicalAssetSearchText(row, ctx).includes(search))
  )
}

/** Distinct kinds of the rows, in German sort order, for the Typ filter. */
function technicalAssetKindOptions(rows) {
  return [...new Set(rows.map((row) => row.typ).filter(Boolean))].sort((a, b) =>
    String(a).localeCompare(String(b), 'de')
  )
}

/** Clicking a header cycles none → ascending → descending → none. */
function nextTechnicalAssetSort(current, key) {
  if (!current || current.key !== key) return { key, dir: 'asc' }
  if (current.dir === 'asc') return { key, dir: 'desc' }
  return null
}

function taCompareSortValues(a, b) {
  const aEmpty = a == null || a === ''
  const bEmpty = b == null || b === ''
  if (aEmpty && bEmpty) return 0
  if (aEmpty) return 1
  if (bEmpty) return -1
  if (typeof a === 'number' && typeof b === 'number') return a - b
  return String(a).localeCompare(String(b), 'de', {
    numeric: true,
    sensitivity: 'base',
  })
}

/**
 * Stable sort by the column whose `key` matches `sort.key`, using its
 * `sortValue(row, ctx)`. Empty values stay last in both directions; without a
 * sort the incoming order (building, then name) is kept.
 */
function sortTechnicalAssetRows(rows, sort, columns, ctx) {
  if (!sort) return rows.slice()
  const column = (columns || []).find((c) => c.key === sort.key)
  if (!column || typeof column.sortValue !== 'function') return rows.slice()
  const direction = sort.dir === 'desc' ? -1 : 1
  return rows
    .map((row, index) => ({ row, index, value: column.sortValue(row, ctx) }))
    .sort((x, y) => {
      const xEmpty = x.value == null || x.value === ''
      const yEmpty = y.value == null || y.value === ''
      if (xEmpty !== yEmpty) return xEmpty ? 1 : -1
      return (
        taCompareSortValues(x.value, y.value) * direction || x.index - y.index
      )
    })
    .map((entry) => entry.row)
}

/** @returns {{ page: number, pages: number, visible: Array<object> }} page clamped to the available range */
function pageTechnicalAssetRows(
  rows,
  page,
  pageSize = TECHNICAL_ASSETS_PAGE_SIZE
) {
  const pages = Math.max(1, Math.ceil(rows.length / pageSize))
  const current = Math.min(Math.max(1, Number(page) || 1), pages)
  return {
    page: current,
    pages,
    visible: rows.slice((current - 1) * pageSize, current * pageSize),
  }
}

if (typeof module !== 'undefined' && module.exports) {
  module.exports = {
    TECHNICAL_ASSETS_PAGE_SIZE,
    emptyTechnicalAssetView,
    technicalAssetSearchText,
    filterTechnicalAssetRows,
    technicalAssetKindOptions,
    nextTechnicalAssetSort,
    sortTechnicalAssetRows,
    pageTechnicalAssetRows,
  }
}
